import React, { FC, ReactNode, useEffect } from "react";
import TextField from "@mui/material/TextField";
import {
  IFormField,
  deepDerefrencer,
  getDecimalPlaces,
} from "../../../utils/form_factory";
import uniqueId from "../../../utils/generateId";
import { ukoTheme } from "../../../theme";
import { Tooltip } from "@mui/material";
import Zoom from "@mui/material/Zoom";

export interface IFormTextInputProps extends Partial<IFormField> {
  name: string;
  type?: string;
  multiline?: boolean;
  rows?: number;
  formControl?: any;
  isDisabled?: boolean;
  isHidden?: boolean;
  decimalPlaces?: number;
  max?: number;
  min?: number;
  placeHolder?: string;
  hint?: ReactNode | string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  value?: any;
}

const FormTextInput: FC<IFormTextInputProps> = ({
  ref,
  name,
  label,
  formControl,
  type,
  rows,
  multiline,
  isDisabled,
  isHidden,
  decimalPlaces = 2,
  max,
  min,
  placeHolder,
  styles: sx,
  hint,
  onChange,
  value: propValue,
}) => {
  const theme = ukoTheme();

  // should determine if field is required based on the formcontrol property
  const isFieldRequired = (fieldName: string) => {
    return String(formControl.getFieldMeta(name)?.error).match(/required/gi)
      ? true
      : false;
  };

  // for handling number input
  const formatNumber = (value: string, isPeriod: boolean) => {
    const floatingNumber = value.toString().split(".");

    if (floatingNumber.length > 1) {
      const decimalPart = floatingNumber[1];
      if (decimalPart.length > decimalPlaces) {
        floatingNumber[1] = decimalPart.slice(0, decimalPlaces);
      }
    }

    return floatingNumber[0]
      .toString()
      .replace(/\D/g, "")
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
      .concat(isPeriod ? "." + floatingNumber[1].replace(/\D/g, "") : "");
  };

  const [formattedValue, setFormattedValue] = React.useState<string>("");

  const displayValue =
    propValue !== undefined
      ? propValue
      : deepDerefrencer(formControl.values, name);

  useEffect(() => {
    if (type === "number") {
      const valueToFormat =
        propValue !== undefined
          ? propValue
          : deepDerefrencer(formControl.values, name);
      if (valueToFormat !== undefined && valueToFormat !== null) {
        const periodIndex = valueToFormat.toString().indexOf(".");
        setFormattedValue(formatNumber(valueToFormat, periodIndex > -1));
      }
    }
  }, [formControl.values, name, type, propValue]);

  const handleNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    const numericInput =
      input.toString().replace(/,/g, "") === ""
        ? ""
        : input.toString().replace(/,/g, "");

    // handle max and min
    if (max !== undefined && parseFloat(numericInput) > max) {
      return;
    }

    if (min !== undefined && parseFloat(numericInput) < min) {
      return;
    }

    // allow only one decimal point
    const decimalCount = (numericInput.match(/\./g) || []).length;
    if (decimalCount > 1) {
      return;
    }

    // handle floating point numbers
    const decimalIndex = numericInput.indexOf(".");
    if (decimalIndex !== -1) {
      const decimalPart = numericInput.slice(decimalIndex + 1);
      if (decimalPart.length > decimalPlaces) {
        return;
      }
    }

    const floatNumber = numericInput.split(".");

    // sets value in formik and removes non numeric characters
    const rawValue = parseFloat(
      floatNumber[0]
        .replace(/\D/g, "")
        .concat(
          decimalIndex > -1 ? "." + floatNumber[1].replace(/\D/g, "") : "",
        ),
    );
    formControl.setFieldValue(name, rawValue);
    formControl.handleChange({
      ...event,
      target: { ...event.target, value: rawValue },
    });

    const formattedInput = formatNumber(numericInput, decimalIndex > -1);
    setFormattedValue(formattedInput);

    // Call custom onChange if provided
    if (onChange) {
      onChange(event);
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    // Call the original formControl.handleBlur
    formControl?.handleBlur && formControl?.handleBlur(event);

    // For number inputs, apply decimal places when user is done with input
    if (
      type === "number" &&
      deepDerefrencer(formControl.values, name) !== undefined
    ) {
      const value = deepDerefrencer(formControl.values, name);
      const calculatedDecimalPlaces = getDecimalPlaces(decimalPlaces);

      if (value !== null && value !== "") {
        // Format to the correct number of decimal places
        const formattedValue = Number(value).toFixed(calculatedDecimalPlaces);

        const PFValue = parseFloat(formattedValue);

        if (!isNaN(PFValue)) {
          formControl.setFieldValue(name, PFValue);

          // Update the formatted display value
          const periodIndex = formattedValue.indexOf(".");
          setFormattedValue(formatNumber(formattedValue, periodIndex > -1));
        } else {
          formControl.setFieldValue(name, null);
        }
      }
    }
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // If custom onChange is provided
    if (onChange) {
      onChange(event);
    } else {
      // Otherwise use the default formControl.handleChange
      formControl.handleChange(event);
    }
  };

  return (
    <Tooltip
      title={hint}
      slots={{
        transition: Zoom,
      }}
    >
      <TextField
        ref={ref}
        sx={[
          styles.customStyles(
            theme,
            isFieldRequired(name) ? theme.palette.primary.main : undefined,
          ),
          sx !== undefined ? (sx as any) : {},
        ]}
        fullWidth
        id={uniqueId()}
        label={label}
        variant="outlined"
        placeholder={placeHolder || ""}
        name={name}
        type={type === "number" ? "text" : type}
        onChange={type === "number" ? handleNumberChange : handleTextChange}
        value={type === "number" ? formattedValue : displayValue}
        error={Boolean(
          deepDerefrencer(formControl.touched, name) &&
            deepDerefrencer(formControl.errors, name),
        )}
        rows={type === "textarea" && multiline ? rows : undefined}
        multiline={type === "textarea" ? multiline : false}
        helperText={
          deepDerefrencer(formControl.touched, name) &&
          deepDerefrencer(formControl.errors, name)
        }
        onBlur={handleBlur}
        disabled={isDisabled}
        hidden={isHidden}
      />
    </Tooltip>
  );
};

const styles = {
  customStyles: (theme: any, outlineColor?: string) => ({
    "& .MuiOutlinedInput-notchedOutline": {
      borderRadius: "8px",
      border: "2px solid",
      borderColor: outlineColor
        ? outlineColor
        : theme.palette.mode === "light"
        ? theme.palette.secondary[300]
        : theme.palette.divider,
    },

    "& input[type='file'].MuiInputBase-input": {
      marginLeft: "120px",
    },
  }),
};

export default FormTextInput;
